import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import trainerService, { type TrainerMonster } from '@services/trainerService';
import monsterService from '@services/monsterService';
import itemsService, { type Item } from '@services/itemsService';
import itemSessionService from '@services/itemSessionService';
import type { ItemDetailData } from '@components/items/ItemDetailModal';
import { useAuth } from '@contexts/useAuth';
import { useDocumentTitle } from '@hooks/useDocumentTitle';
import type { Trainer, TrainerRelation } from '@components/trainers/types/Trainer';
import type { MonsterBoxPosition } from '../detail/shared/boxSortUtils';

// --- Types ---

export interface FeaturedMonster extends TrainerMonster {
  display_order?: number;
  image_url?: string;
}

export interface Achievement {
  id: number;
  name: string;
  description?: string;
  category?: string;
  progress?: number;
  target?: number;
  requirement?: number;
  unlocked?: boolean;
  claimed?: boolean;
  canClaim?: boolean;
  reward?: {
    currency?: number;
    levels?: number;
    items?: { name: string; quantity: number }[];
    item?: string;
  };
  [key: string]: unknown;
}

export interface AchievementStats {
  total: number;
  unlocked: number;
  claimed: number;
  unclaimed: number;
  [key: string]: unknown;
}

export interface RewardPopupData {
  isBulk?: boolean;
  achievement?: Achievement;
  rewards?: { currency?: number; levels?: number; items?: { name: string; quantity: number }[]; item?: string };
  claimedCount?: number;
  claimedAchievements?: Achievement[];
  totalRewards?: { currency?: number; levels?: number; items?: { name: string; quantity: number }[] };
  message?: string;
}

export interface TrainerGalleryImage {
  id: number;
  image_url: string;
  title: string | null;
  created_at: string;
  is_mature: boolean;
  content_rating: Record<string, boolean> | null;
}

interface InventoryData {
  [category: string]: Record<string, number>;
}

// --- Helper functions ---

export function toTitleCase(str: string | undefined | null): string {
  if (!str) return '';
  return str.split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

export function formatMonsterData(monster: TrainerMonster): TrainerMonster {
  if (!monster) return monster;
  const formatted = { ...monster };

  const speciesFields = ['species1', 'species2', 'species3'] as const;
  const typeFields = ['type1', 'type2', 'type3', 'type4', 'type5'] as const;

  for (const field of speciesFields) {
    if (formatted[field]) formatted[field] = toTitleCase(formatted[field] as string);
  }
  for (const field of typeFields) {
    if (formatted[field]) formatted[field] = toTitleCase(formatted[field] as string);
  }
  if (formatted.attribute) formatted.attribute = toTitleCase(formatted.attribute as string);

  return formatted;
}

export function calculateDisplayAge(ageValue: string | number | undefined, birthday?: string): string | number | null {
  if (!ageValue) return null;
  const ageStr = String(ageValue).trim();

  if (!/^\d+$/.test(ageStr)) return ageStr;

  if (birthday) {
    const parts = birthday.split('-').map(Number);
    if (parts.length === 3 && !parts.some(isNaN)) {
      const today = new Date();
      let age = today.getFullYear() - parts[0];
      const monthDiff = today.getMonth() + 1 - parts[1];
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < parts[2])) {
        age--;
      }
      return age;
    }
  }

  return ageStr;
}

// --- Hook ---

export function useTrainerDetail() {
  const { id } = useParams<{ id: string }>();
  const { currentUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  // Core data
  const [trainer, setTrainer] = useState<Trainer | null>(null);
  const [monsters, setMonsters] = useState<TrainerMonster[]>([]);
  const [galleryImages, setGalleryImages] = useState<TrainerGalleryImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Tab state - controlled via URL
  const activeTab = searchParams.get('tab') || 'profile';
  const setActiveTab = useCallback((tab: string) => {
    setSearchParams({ tab }, { replace: true });
  }, [setSearchParams]);

  // PC box state
  const [currentPCBox, setCurrentPCBox] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  // Edit boxes state
  const [boxMonsters, setBoxMonsters] = useState<TrainerMonster[]>([]);
  const [featuredMonsters, setFeaturedMonsters] = useState<(FeaturedMonster | null)[]>(
    Array.from({ length: 6 }, () => null)
  );
  const [featuredMonstersLoaded, setFeaturedMonstersLoaded] = useState(false);
  const [draggedMonster, setDraggedMonster] = useState<TrainerMonster | null>(null);
  const [dragSourceBox, setDragSourceBox] = useState<number | null>(null);
  const [dragSourceSlot, setDragSourceSlot] = useState<number | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [statusType, setStatusType] = useState<'info' | 'success' | 'error' | 'warning'>('info');
  const [additionalBoxes, setAdditionalBoxes] = useState(0);
  const [featuredMonstersCollapsed, setFeaturedMonstersCollapsed] = useState(false);

  // Box settings state (lock/default)
  const [boxSettings, setBoxSettings] = useState<{ boxNumber: number; isLocked: boolean; isDefault: boolean }[]>([]);
  const [showAutoSortModal, setShowAutoSortModal] = useState(false);

  // Inventory state
  const [inventoryData, setInventoryData] = useState<InventoryData | null>(null);
  const [inventoryItemDetails, setInventoryItemDetails] = useState<Record<string, Item>>({});
  const [isItemDetailModalOpen, setIsItemDetailModalOpen] = useState(false);
  const [selectedItemForDetail, setSelectedItemForDetail] = useState<ItemDetailData | null>(null);

  // Achievement state
  const [achievements, setAchievements] = useState<Achievement[]>([]);
  const [achievementStats, setAchievementStats] = useState<AchievementStats | null>(null);
  const [achievementsLoading, setAchievementsLoading] = useState(false);
  const [achievementFilter, setAchievementFilter] = useState('all');

  // Reward popup state
  const [showRewardPopup, setShowRewardPopup] = useState(false);
  const [rewardPopupData, setRewardPopupData] = useState<RewardPopupData | null>(null);
  const [isClaimingAll, setIsClaimingAll] = useState(false);

  // Mass edit state
  const [showMassEditModal, setShowMassEditModal] = useState(false);
  const [trainerInventory, setTrainerInventory] = useState<Record<string, Record<string, number>>>({});
  const [hasPendingMassEditSession, setHasPendingMassEditSession] = useState(false);

  // Relations state
  const [relatedTrainers, setRelatedTrainers] = useState<Record<string, Trainer>>({});
  const [relatedMonsters, setRelatedMonsters] = useState<Record<string, unknown>>({});

  // Image modal state
  const [showImageModal, setShowImageModal] = useState(false);
  const [modalImageSrc, setModalImageSrc] = useState('');
  const [modalImageAlt, setModalImageAlt] = useState('');

  // Document title
  useDocumentTitle(trainer ? trainer.name : 'Trainer');

  // --- Computed values ---

  const isOwner = useMemo(() => {
    if (!currentUser || !trainer) return false;
    return (
      String(currentUser.id) === String(trainer.player_user_id) ||
      currentUser.username === trainer.player_user_id ||
      (!!currentUser.email && currentUser.email === trainer.player_user_id) ||
      (!!currentUser.discord_id && currentUser.discord_id === trainer.player_user_id) ||
      currentUser.is_admin === true
    );
  }, [currentUser, trainer]);

  const filteredMonsters = useMemo(() => {
    if (!searchTerm) return monsters;
    const term = searchTerm.toLowerCase();
    return monsters.filter(m =>
      m.name?.toLowerCase().includes(term) ||
      (m.species1 as string | undefined)?.toLowerCase().includes(term) ||
      (m.species2 as string | undefined)?.toLowerCase().includes(term) ||
      (m.species3 as string | undefined)?.toLowerCase().includes(term) ||
      (m.type1 as string | undefined)?.toLowerCase().includes(term) ||
      (m.type2 as string | undefined)?.toLowerCase().includes(term) ||
      (m.type3 as string | undefined)?.toLowerCase().includes(term) ||
      (m.type4 as string | undefined)?.toLowerCase().includes(term) ||
      (m.type5 as string | undefined)?.toLowerCase().includes(term)
    );
  }, [monsters, searchTerm]);

  // --- Box helper functions ---

  const getMaxBoxNumber = useCallback(() => {
    if (searchTerm) {
      return Math.max(1, Math.ceil(filteredMonsters.length / 30));
    }
    let maxBox = 0;
    if (boxMonsters.length > 0) {
      maxBox = Math.max(...boxMonsters.map(m =>
        m && m.box_number != null ? m.box_number : 0
      ));
    }
    return maxBox + 1 + additionalBoxes;
  }, [searchTerm, filteredMonsters.length, boxMonsters, additionalBoxes]);

  const getBoxMonsters = useCallback((boxIndex: number): (TrainerMonster | null)[] => {
    const boxSlots: (TrainerMonster | null)[] = new Array(30).fill(null);
    boxMonsters.forEach(monster => {
      if (monster &&
        monster.box_number === boxIndex &&
        monster.trainer_index != null &&
        (monster.trainer_index as number) >= 0 &&
        (monster.trainer_index as number) < 30) {
        boxSlots[monster.trainer_index as number] = monster;
      }
    });
    return boxSlots;
  }, [boxMonsters]);

  const getBoxMonstersForDisplay = useCallback((boxIndex: number): (TrainerMonster | null)[] => {
    const boxSlots: (TrainerMonster | null)[] = new Array(30).fill(null);
    filteredMonsters.forEach(monster => {
      if (monster &&
        monster.box_number === boxIndex &&
        monster.trainer_index != null &&
        (monster.trainer_index as number) >= 0 &&
        (monster.trainer_index as number) < 30) {
        boxSlots[monster.trainer_index as number] = monster;
      }
    });
    return boxSlots;
  }, [filteredMonsters]);

  const getFilteredBoxMonsters = useCallback((boxIndex: number): (TrainerMonster | null)[] => {
    const boxSlots: (TrainerMonster | null)[] = new Array(30).fill(null);
    const startIndex = boxIndex * 30;
    const monstersForBox = filteredMonsters.slice(startIndex, startIndex + 30);
    monstersForBox.forEach((monster, index) => {
      boxSlots[index] = monster;
    });
    return boxSlots;
  }, [filteredMonsters]);

  // --- Data fetching ---

  const fetchTrainerData = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      const trainerData = await trainerService.getTrainer(id);

      const monstersResponse = await trainerService.getTrainerMonsters(id, { limit: 1000 });
      const rawMonsters = monstersResponse.monsters || [];
      const formattedMonsters = rawMonsters.map(m => formatMonsterData(m));
      setMonsters(formattedMonsters);

      if (trainerData) {
        // Fetch inventory
        try {
          const invResponse = await trainerService.getTrainerInventory(id);
          const invData = invResponse as unknown as { success?: boolean; data?: InventoryData };
          if (invData?.success && invData.data) {
            setInventoryData(invData.data);
            // Fetch item details
            fetchInventoryItemDetails(invData.data);
          }
        } catch (e) {
          console.error('Error fetching inventory:', e);
        }

        // Parse additional_refs
        if (trainerData.additional_refs) {
          if (typeof trainerData.additional_refs === 'string') {
            try {
              trainerData.additional_refs = JSON.parse(trainerData.additional_refs);
            } catch {
              trainerData.additional_refs = [];
            }
          }
          if (!Array.isArray(trainerData.additional_refs)) {
            trainerData.additional_refs = [];
          }
        }

        // Fetch gallery (submissions featuring this trainer)
        try {
          const galleryResponse = await trainerService.getTrainerGallery(id);
          if (galleryResponse?.success && Array.isArray(galleryResponse.data)) {
            setGalleryImages(galleryResponse.data);
          } else {
            setGalleryImages([]);
          }
        } catch {
          setGalleryImages([]);
        }

        setTrainer(trainerData);
      } else {
        setTrainer(null);
      }
    } catch (err) {
      console.error(`Error fetching trainer ${id}:`, err);
      setError('Failed to load trainer data. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchInventoryItemDetails = async (inventory: InventoryData) => {
    const itemDetails: Record<string, Item> = {};
    const allItemNames = new Set<string>();
    Object.values(inventory).forEach(category => {
      if (category && typeof category === 'object') {
        Object.keys(category).forEach(name => allItemNames.add(name));
      }
    });

    for (const itemName of allItemNames) {
      try {
        const response = await itemsService.getItems({ search: itemName, limit: 50 });
        if (response.data?.length > 0) {
          const found = response.data.find(item =>
            item.name.toLowerCase() === itemName.toLowerCase()
          );
          if (found) itemDetails[itemName] = found;
        }
      } catch {
        // skip individual item fetch errors
      }
    }
    setInventoryItemDetails(itemDetails);
  };

  const fetchAchievements = useCallback(async () => {
    if (!id) return;
    try {
      setAchievementsLoading(true);
      const response = await trainerService.getAchievements(id);
      if (response.success) {
        const loadedAchievements = response.data?.achievements || [];
        setAchievements(loadedAchievements);

        // Fetch item details for achievement reward items (for images)
        const achievementItemNames = new Set<string>();
        for (const a of loadedAchievements) {
          if (a.reward?.items) {
            for (const item of a.reward.items) {
              achievementItemNames.add(item.name);
            }
          }
          if (a.reward?.item) {
            achievementItemNames.add(a.reward.item);
          }
        }
        if (achievementItemNames.size > 0) {
          try {
            // Fetch all items in one call and build lookup map
            const allItemsResponse = await itemsService.getItems({ limit: 1000 });
            if (allItemsResponse.data?.length > 0) {
              const newDetails: Record<string, Item> = {};
              for (const itemName of achievementItemNames) {
                const found = allItemsResponse.data.find(item =>
                  item.name.toLowerCase() === itemName.toLowerCase()
                );
                if (found) newDetails[itemName] = found;
              }
              if (Object.keys(newDetails).length > 0) {
                setInventoryItemDetails(prev => ({ ...prev, ...newDetails }));
              }
            }
          } catch {
            // skip item detail fetch errors
          }
        }

        const statsResponse = await trainerService.getAchievementStats(id);
        if (statsResponse.success) {
          const raw = statsResponse.data?.stats;
          if (raw) {
            setAchievementStats({
              total: raw.totalAchievements ?? raw.total ?? 0,
              unlocked: raw.unlockedCount ?? raw.unlocked ?? 0,
              claimed: raw.claimedCount ?? raw.claimed ?? 0,
              unclaimed: (raw.unlockedCount ?? raw.unlocked ?? 0) - (raw.claimedCount ?? raw.claimed ?? 0),
            });
          } else {
            setAchievementStats(null);
          }
        }
      }
    } catch {
      setAchievements([]);
      setAchievementStats(null);
    } finally {
      setAchievementsLoading(false);
    }
  }, [id]);

  const fetchRelatedEntities = useCallback(async () => {
    if (!trainer?.relations) return;
    try {
      const relations: TrainerRelation[] = typeof trainer.relations === 'string'
        ? JSON.parse(trainer.relations)
        : trainer.relations;
      if (!Array.isArray(relations)) return;

      const trainersData: Record<string, Trainer> = {};
      const monstersData: Record<string, unknown> = {};

      // Relations are stored with trainer_id and monster_id fields (not target_id)
      const trainerRelations = relations.filter(r => r.type === 'trainer');
      const monsterRelations = relations.filter(r => r.type === 'monster');

      for (const rel of trainerRelations) {
        const tid = (rel as unknown as Record<string, unknown>).trainer_id || rel.target_id;
        if (!tid) continue;
        try {
          const t = await trainerService.getTrainer(tid as number);
          if (t) trainersData[String(tid)] = t;
        } catch { /* skip */ }
      }

      for (const rel of monsterRelations) {
        const mid = (rel as unknown as Record<string, unknown>).monster_id || rel.target_id;
        if (!mid) continue;
        try {
          const response = await monsterService.getMonsterById(mid as number);
          if (response?.success && response.data) {
            monstersData[String(mid)] = response.data;
          }
        } catch { /* skip */ }
      }

      setRelatedTrainers(trainersData);
      setRelatedMonsters(monstersData);
    } catch {
      // parse error
    }
  }, [trainer]);

  // --- Achievement handlers ---

  const handleClaimAchievement = useCallback(async (achievementId: number) => {
    if (!id) return;
    try {
      const response = await trainerService.claimAchievement(id, achievementId);
      if (response.success) {
        const reward = response.data.reward || response.data.rewards;
        setRewardPopupData({
          achievement: response.data.achievement,
          rewards: reward,
        });
        setShowRewardPopup(true);

        setAchievements(prev => prev.map(a =>
          a.id === achievementId ? { ...a, claimed: true, canClaim: false } : a
        ));

        if (reward?.currency) {
          setTrainer(prev => prev ? {
            ...prev,
            currency_amount: (prev.currency_amount || 0) + reward.currency,
            total_earned_currency: (prev.total_earned_currency || 0) + reward.currency,
          } : prev);
        }
        if (reward?.levels) {
          setTrainer(prev => prev ? {
            ...prev,
            level: (prev.level || 0) + reward.levels,
          } : prev);
        }

        setAchievementStats(prev => prev ? {
          ...prev,
          claimed: prev.claimed + 1,
          unclaimed: prev.unclaimed - 1,
        } : prev);
      }
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      setStatusMessage(axiosErr.response?.data?.message || 'Failed to claim achievement');
      setStatusType('error');
    }
  }, [id]);

  const handleClaimAllAchievements = useCallback(async () => {
    if (!id) return;
    try {
      setIsClaimingAll(true);
      const response = await trainerService.claimAllAchievements(id);
      if (response.success) {
        const normalizedAchievements = (response.data.claimedAchievements || []).map(
          (a: { id: string; name: string; reward?: { currency?: number; levels?: number; items?: { name: string; quantity: number }[]; item?: string } }) => ({
            ...a,
          })
        );
        setRewardPopupData({
          isBulk: true,
          claimedCount: response.data.claimedCount,
          claimedAchievements: normalizedAchievements,
          totalRewards: response.data.totalRewards,
          message: response.message,
        });
        setShowRewardPopup(true);

        setAchievements(prev => prev.map(a => {
          const wasClaimed = response.data.claimedAchievements?.some(
            (c: Achievement) => c.id === a.id
          );
          return wasClaimed ? { ...a, claimed: true, canClaim: false } : a;
        }));

        if (response.data.totalRewards?.currency > 0) {
          setTrainer(prev => prev ? {
            ...prev,
            currency_amount: (prev.currency_amount || 0) + response.data.totalRewards.currency,
            total_earned_currency: (prev.total_earned_currency || 0) + response.data.totalRewards.currency,
          } : prev);
        }
        if (response.data.totalRewards?.levels > 0) {
          setTrainer(prev => prev ? {
            ...prev,
            level: (prev.level || 0) + response.data.totalRewards.levels,
          } : prev);
        }

        if (achievementStats && response.data.claimedCount > 0) {
          setAchievementStats(prev => prev ? {
            ...prev,
            claimed: prev.claimed + response.data.claimedCount,
            unclaimed: prev.unclaimed - response.data.claimedCount,
          } : prev);
        }
      }
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      setStatusMessage(axiosErr.response?.data?.message || 'Failed to claim achievements');
      setStatusType('error');
    } finally {
      setIsClaimingAll(false);
    }
  }, [id, achievementStats]);

  // --- Item detail handler ---

  const handleItemDetailClick = useCallback(async (itemName: string, category: string) => {
    const itemToDetailData = (item: Item): ItemDetailData => ({
      id: String(item.id),
      name: item.name,
      description: item.description,
      effect: item.effect,
      category: item.category,
      rarity: item.rarity,
      image_url: item.image_url,
      image_path: item.image_path,
    });

    try {
      const response = await itemsService.getItems({ search: itemName, limit: 50 });
      let foundItem: Item | null = null;
      if (response.data?.length > 0) {
        foundItem = response.data.find(item =>
          item.name.toLowerCase() === itemName.toLowerCase()
        ) || response.data[0];
      }
      setSelectedItemForDetail(foundItem ? itemToDetailData(foundItem) : {
        name: itemName,
        category: category || 'items',
        description: `Information about ${itemName}`,
        effect: `Item effect for ${itemName}`,
      });
      setIsItemDetailModalOpen(true);
    } catch {
      setSelectedItemForDetail({
        name: itemName,
        category: category || 'items',
        description: `Information about ${itemName}`,
      });
      setIsItemDetailModalOpen(true);
    }
  }, []);

  const getItemImageUrl = useCallback((itemName: string, category: string = 'items'): string => {
    const detail = inventoryItemDetails[itemName];
    if (detail?.image_url) return detail.image_url;
    if (category === 'keyitems') {
      return `/images/items/keyitems/${itemName.toLowerCase().replace(/\s+/g, '_')}.png`;
    }
    return `/images/items/${itemName.toLowerCase().replace(/\s+/g, '_')}.png`;
  }, [inventoryItemDetails]);

  // --- Mass edit ---

  // Check for pending mass edit session
  useEffect(() => {
    if (!isOwner) return;
    itemSessionService.get('mass_edit').then(session => {
      setHasPendingMassEditSession(!!session);
    }).catch(() => {});
  }, [isOwner, showMassEditModal]);

  const handleOpenMassEdit = useCallback(async () => {
    if (!id) return;
    try {
      const invResponse = await trainerService.getTrainerInventory(id);
      const invData = invResponse as unknown as { success?: boolean; data?: InventoryData };
      if (invData?.success && invData.data) {
        setTrainerInventory({
          berries: (invData.data.berries as unknown as Record<string, number>) || {},
          pastries: (invData.data.pastries as unknown as Record<string, number>) || {},
        });
      }
    } catch {
      // skip
    }
    setShowMassEditModal(true);
  }, [id]);

  const handleMassEditComplete = useCallback((results: Array<{ status: string }>) => {
    fetchTrainerData();
    setShowMassEditModal(false);
    setHasPendingMassEditSession(false);
    const successCount = results.filter(r => r.status === 'success').length;
    setStatusMessage(`Mass edit completed! ${successCount} operations processed successfully.`);
    setStatusType('success');
    setTimeout(() => setStatusMessage(''), 5000);
  }, [fetchTrainerData]);

  // --- Box settings ---

  const isBoxLocked = useCallback((boxNum: number): boolean => {
    return boxSettings.some(s => s.boxNumber === boxNum && s.isLocked);
  }, [boxSettings]);

  const isDefaultBox = useCallback((boxNum: number): boolean => {
    return boxSettings.some(s => s.boxNumber === boxNum && s.isDefault);
  }, [boxSettings]);

  const toggleBoxLock = useCallback((boxNum: number) => {
    setBoxSettings(prev => {
      const existing = prev.find(s => s.boxNumber === boxNum);
      if (existing) {
        return prev.map(s =>
          s.boxNumber === boxNum ? { ...s, isLocked: !s.isLocked } : s
        );
      }
      return [...prev, { boxNumber: boxNum, isLocked: true, isDefault: false }];
    });
  }, []);

  const setDefaultBox = useCallback((boxNum: number) => {
    setBoxSettings(prev => {
      const wasDefault = prev.some(s => s.boxNumber === boxNum && s.isDefault);
      const existing = prev.find(s => s.boxNumber === boxNum);
      if (existing) {
        return prev.map(s => ({
          ...s,
          isDefault: s.boxNumber === boxNum ? !wasDefault : false,
        }));
      }
      // Clear all defaults and add new entry
      return [
        ...prev.map(s => ({ ...s, isDefault: false })),
        { boxNumber: boxNum, isLocked: false, isDefault: true },
      ];
    });
  }, []);

  const lockedBoxNumbers = useMemo(() => {
    return new Set(boxSettings.filter(s => s.isLocked).map(s => s.boxNumber));
  }, [boxSettings]);

  const handleSwapBoxes = useCallback((sourceBoxIndex: number, targetBoxIndex: number) => {
    if (sourceBoxIndex === targetBoxIndex) { return; }
    setBoxMonsters(prev => prev.map(m => {
      if (m.box_number === sourceBoxIndex) {
        return { ...m, box_number: targetBoxIndex };
      }
      if (m.box_number === targetBoxIndex) {
        return { ...m, box_number: sourceBoxIndex };
      }
      return m;
    }));
    // Also swap box settings
    setBoxSettings(prev => prev.map(s => {
      if (s.boxNumber === sourceBoxIndex) {
        return { ...s, boxNumber: targetBoxIndex };
      }
      if (s.boxNumber === targetBoxIndex) {
        return { ...s, boxNumber: sourceBoxIndex };
      }
      return s;
    }));
    setStatusMessage(`Swapped Box ${sourceBoxIndex + 1} and Box ${targetBoxIndex + 1}. Remember to save!`);
    setStatusType('info');
  }, []);

  const handleInsertBoxBefore = useCallback((sourceBoxIndex: number, targetBoxIndex: number) => {
    if (sourceBoxIndex === targetBoxIndex) { return; }
    setBoxMonsters(prev => {
      // Collect all unique box numbers
      const allBoxNums = new Set(prev.map(m => m.box_number ?? 0));
      allBoxNums.add(sourceBoxIndex);
      allBoxNums.add(targetBoxIndex);
      const sorted = Array.from(allBoxNums).sort((a, b) => a - b);

      // Build new order: remove source, insert before target
      const withoutSource = sorted.filter(n => n !== sourceBoxIndex);
      const insertIdx = withoutSource.indexOf(targetBoxIndex);
      const newOrder = [...withoutSource.slice(0, insertIdx), sourceBoxIndex, ...withoutSource.slice(insertIdx)];

      // Map old box numbers to new sequential positions
      const boxMap = new Map<number, number>();
      newOrder.forEach((oldBox, newIdx) => {
        // The new box number is the position in sorted order
        boxMap.set(oldBox, sorted[newIdx]);
      });

      return prev.map(m => {
        const oldBox = m.box_number ?? 0;
        const newBox = boxMap.get(oldBox);
        if (newBox != null && newBox !== oldBox) {
          return { ...m, box_number: newBox };
        }
        return m;
      });
    });
    // Also remap box settings
    setBoxSettings(prev => {
      const allBoxNums = new Set(prev.map(s => s.boxNumber));
      allBoxNums.add(sourceBoxIndex);
      allBoxNums.add(targetBoxIndex);
      const sorted = Array.from(allBoxNums).sort((a, b) => a - b);

      const withoutSource = sorted.filter(n => n !== sourceBoxIndex);
      const insertIdx = withoutSource.indexOf(targetBoxIndex);
      const newOrder = [...withoutSource.slice(0, insertIdx), sourceBoxIndex, ...withoutSource.slice(insertIdx)];

      const boxMap = new Map<number, number>();
      newOrder.forEach((oldBox, newIdx) => {
        boxMap.set(oldBox, sorted[newIdx]);
      });

      return prev.map(s => {
        const newBox = boxMap.get(s.boxNumber);
        if (newBox != null) {
          return { ...s, boxNumber: newBox };
        }
        return s;
      });
    });
    setStatusMessage(`Moved Box ${sourceBoxIndex + 1} before Box ${targetBoxIndex + 1}. Remember to save!`);
    setStatusType('info');
  }, []);

  const handleApplySort = useCallback((positions: MonsterBoxPosition[]) => {
    setBoxMonsters(prev => {
      const monsterMap = new Map(prev.map(m => [m.id, m]));
      return positions
        .map(pos => {
          const monster = monsterMap.get(pos.id);
          if (!monster) return null;
          return {
            ...monster,
            box_number: pos.boxNumber,
            trainer_index: pos.trainerIndex,
          };
        })
        .filter(Boolean) as TrainerMonster[];
    });
    setStatusMessage('Monsters sorted! Review the result and click "Save Changes" to persist.');
    setStatusType('info');
  }, []);

  const fetchBoxSettings = useCallback(async () => {
    if (!id) return;
    try {
      const response = await trainerService.getBoxSettings(id);
      if (response.success) {
        setBoxSettings(response.settings);
      }
    } catch {
      // skip
    }
  }, [id]);

  const handleSaveBoxSettings = useCallback(async () => {
    if (!id) return;
    try {
      // Only save settings that have at least one flag set
      const toSave = boxSettings.filter(s => s.isLocked || s.isDefault);
      await trainerService.updateBoxSettings(id, toSave);
    } catch (err) {
      console.error('Error saving box settings:', err);
    }
  }, [id, boxSettings]);

  // --- Drag and drop ---

  const handleDragStart = useCallback((
    e: React.DragEvent,
    monster: TrainerMonster | null,
    boxIndex: number,
    slotIndex: number
  ) => {
    if (!monster) return;
    setDraggedMonster(monster);
    setDragSourceBox(boxIndex);
    setDragSourceSlot(slotIndex);
    e.currentTarget.classList.add('dragging');
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', JSON.stringify({
      monsterId: monster.id,
      sourceBox: boxIndex,
      sourceSlot: slotIndex,
    }));
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }, []);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.currentTarget.classList.add('drag-over');
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.currentTarget.classList.remove('drag-over');
  }, []);

  const handleDrop = useCallback((e: React.DragEvent, targetBoxIndex: number, targetSlotIndex: number) => {
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.classList.remove('drag-over');

    if (!draggedMonster) return;

    if (dragSourceBox === -1) {
      // From featured to regular box
      const updated = [...boxMonsters];
      const targetMonster = updated.find(m =>
        m && m.box_number === targetBoxIndex && (m.trainer_index as number) === targetSlotIndex
      );

      const updatedDragged = { ...draggedMonster, box_number: targetBoxIndex, trainer_index: targetSlotIndex };

      // The dragged monster's original box position
      const origBox = draggedMonster.box_number;
      const origSlot = draggedMonster.trainer_index as number;

      // Move dragged monster to the new position
      const existingIdx = updated.findIndex(m => m && m.id === draggedMonster.id);
      if (existingIdx !== -1) {
        updated[existingIdx] = { ...draggedMonster, box_number: targetBoxIndex, trainer_index: targetSlotIndex };
      } else {
        updated.push(updatedDragged);
      }

      // Swap: target monster goes to the dragged monster's original box position
      if (targetMonster) {
        const targetIdx = updated.findIndex(m => m && m.id === targetMonster.id);
        if (targetIdx !== -1) {
          updated[targetIdx] = { ...targetMonster, box_number: origBox, trainer_index: origSlot };
        }
      }
      setBoxMonsters(updated);

      // Remove from featured
      const updatedFeatured = [...featuredMonsters];
      if (dragSourceSlot != null) updatedFeatured[dragSourceSlot] = null;
      setFeaturedMonsters(updatedFeatured);
    } else {
      // Box-to-box
      const updated = [...boxMonsters];
      const targetMonster = updated.find(m =>
        m && m.box_number === targetBoxIndex && (m.trainer_index as number) === targetSlotIndex
      );

      const draggedIdx = updated.findIndex(m => m && m.id === draggedMonster.id);
      if (draggedIdx !== -1) {
        updated[draggedIdx] = { ...draggedMonster, box_number: targetBoxIndex, trainer_index: targetSlotIndex };
      }

      if (targetMonster) {
        const targetIdx = updated.findIndex(m => m && m.id === targetMonster.id);
        if (targetIdx !== -1) {
          updated[targetIdx] = { ...targetMonster, box_number: dragSourceBox!, trainer_index: dragSourceSlot! };
        }
      }
      setBoxMonsters(updated);
    }

    setDraggedMonster(null);
    setDragSourceBox(null);
    setDragSourceSlot(null);
    setStatusMessage('Monster position updated. Remember to save your changes!');
    setStatusType('info');
  }, [draggedMonster, dragSourceBox, dragSourceSlot, boxMonsters, featuredMonsters]);

  const handleDragEnd = useCallback((e: React.DragEvent) => {
    e.currentTarget.classList.remove('dragging');
    document.querySelectorAll('.edit-box-slot').forEach(slot => {
      slot.classList.remove('drag-over');
    });
  }, []);

  // Featured monster drag handlers
  const handleFeaturedDrop = useCallback((e: React.DragEvent, slotIndex: number) => {
    e.preventDefault();
    if (!draggedMonster) return;

    const updated = Array.from({ length: 6 }, (_, i) => featuredMonsters[i] || null);
    const existingIdx = updated.findIndex(fm => fm && fm.id === draggedMonster.id);

    if (existingIdx !== -1) {
      const target = updated[slotIndex];
      updated[existingIdx] = target;
      updated[slotIndex] = draggedMonster as FeaturedMonster;
    } else {
      updated[slotIndex] = draggedMonster as FeaturedMonster;
    }

    setFeaturedMonsters(updated);
    setStatusMessage('Featured monster updated. Remember to save your changes!');
    setStatusType('info');
  }, [draggedMonster, featuredMonsters]);

  const handleFeaturedDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.currentTarget.classList.add('drag-over');
  }, []);

  const handleFeaturedDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.currentTarget.classList.remove('drag-over');
  }, []);

  // --- Save handlers ---

  const handleSaveBoxes = useCallback(async () => {
    if (isSaving || !id) return;
    setIsSaving(true);
    setStatusMessage('Saving changes...');
    setStatusType('info');

    try {
      const positions = boxMonsters
        .filter(Boolean)
        .map(m => ({
          id: m.id,
          boxNumber: m.box_number ?? 0,
          position: (m.trainer_index as number) ?? 0,
        }));

      // Also save box settings
      await handleSaveBoxSettings();

      const response = await trainerService.updateMonsterBoxPositions(id, positions);

      if (response.success) {
        setStatusMessage('Box positions saved! Now saving featured monsters...');
        try {
          const featuredIds = featuredMonsters.filter(Boolean).map(fm => fm!.id);
          const featuredResponse = await trainerService.updateFeaturedMonsters(id, featuredIds);
          if (featuredResponse.success) {
            setStatusMessage('All changes saved successfully!');
            setStatusType('success');
          } else {
            setStatusMessage('Box positions saved, but featured monsters failed.');
            setStatusType('warning');
          }
        } catch {
          setStatusMessage('Box positions saved, but featured monsters failed.');
          setStatusType('warning');
        }

        // Refresh
        const monstersResponse = await trainerService.getTrainerMonsters(id, { limit: 1000 });
        setMonsters((monstersResponse.monsters || []).map(m => formatMonsterData(m)));
        setFeaturedMonstersLoaded(false);
      } else {
        setStatusMessage('Failed to save box positions');
        setStatusType('error');
      }
    } catch (err: unknown) {
      const msg = (err as Error).message || 'Failed to save box positions';
      setStatusMessage(`Error: ${msg}`);
      setStatusType('error');
    } finally {
      setIsSaving(false);
    }
  }, [isSaving, id, boxMonsters, featuredMonsters, handleSaveBoxSettings]);

  const handleSaveFeaturedMonsters = useCallback(async () => {
    if (isSaving || !id) return;
    setIsSaving(true);
    setStatusMessage('Saving featured monsters...');
    setStatusType('info');

    try {
      const featuredIds = featuredMonsters.filter(Boolean).map(fm => fm!.id);
      const response = await trainerService.updateFeaturedMonsters(id, featuredIds);

      if (response.success) {
        setStatusMessage('Featured monsters saved successfully!');
        setStatusType('success');
        // Refetch
        const updatedResponse = await trainerService.getFeaturedMonsters(id);
        const apiMonsters: FeaturedMonster[] = updatedResponse.featuredMonsters || [];
        const slotArray: (FeaturedMonster | null)[] = Array.from({ length: 6 }, () => null);
        apiMonsters.forEach((monster) => {
          if (monster.display_order && monster.display_order >= 1 && monster.display_order <= 6) {
            slotArray[monster.display_order - 1] = monster;
          }
        });
        setFeaturedMonsters(slotArray);
      } else {
        setStatusMessage('Failed to save featured monsters');
        setStatusType('error');
      }
    } catch (err: unknown) {
      const msg = (err as Error).message || 'Failed to save featured monsters';
      setStatusMessage(`Error: ${msg}`);
      setStatusType('error');
    } finally {
      setIsSaving(false);
    }
  }, [isSaving, id, featuredMonsters]);

  const handleAddBox = useCallback(() => {
    setAdditionalBoxes(prev => prev + 1);
    setStatusMessage('Empty box added! Drag monsters into it. Remember to save!');
    setStatusType('info');
  }, []);

  // --- Image modal ---

  const handleImageClick = useCallback((imageSrc: string, imageAlt: string) => {
    setModalImageSrc(imageSrc);
    setModalImageAlt(imageAlt);
    setShowImageModal(true);
  }, []);

  const closeImageModal = useCallback(() => {
    setShowImageModal(false);
    setModalImageSrc('');
    setModalImageAlt('');
  }, []);

  // --- Effects ---

  // Reset search when changing tabs
  useEffect(() => {
    if (activeTab !== 'pc' && activeTab !== 'boxes') {
      setSearchTerm('');
    }
  }, [activeTab]);

  // Reset PC box on search
  useEffect(() => {
    if (searchTerm) setCurrentPCBox(0);
  }, [searchTerm]);

  // Fetch trainer data on mount / id change
  useEffect(() => {
    setFeaturedMonstersLoaded(false);
    fetchTrainerData();
  }, [id, fetchTrainerData]);

  // Initialize box monsters from monsters
  useEffect(() => {
    if (monsters.length > 0) {
      setBoxMonsters([...monsters]);
      setAdditionalBoxes(0);
    }
  }, [monsters]);

  // Fetch featured monsters
  useEffect(() => {
    if (!id || !trainer || featuredMonstersLoaded) return;
    const fetchFeatured = async () => {
      try {
        const response = await trainerService.getFeaturedMonsters(id);
        const apiMonsters: FeaturedMonster[] = response.featuredMonsters || [];
        const slotArray: (FeaturedMonster | null)[] = Array.from({ length: 6 }, () => null);
        apiMonsters.forEach((monster) => {
          if (monster.display_order && monster.display_order >= 1 && monster.display_order <= 6) {
            slotArray[monster.display_order - 1] = monster;
          }
        });
        setFeaturedMonsters(slotArray);
      } catch {
        setFeaturedMonsters(Array.from({ length: 6 }, () => null));
      }
      setFeaturedMonstersLoaded(true);
    };
    fetchFeatured();
  }, [id, trainer, featuredMonstersLoaded]);

  // Fetch box settings when entering edit-boxes tab
  useEffect(() => {
    if (trainer && activeTab === 'edit-boxes') fetchBoxSettings();
  }, [trainer, activeTab, fetchBoxSettings]);

  // Fetch achievements when tab is opened
  useEffect(() => {
    if (trainer && activeTab === 'achievements') fetchAchievements();
  }, [trainer, activeTab, fetchAchievements]);

  // Fetch related entities when relations tab is opened
  useEffect(() => {
    if (trainer && activeTab === 'relations' && trainer.relations) fetchRelatedEntities();
  }, [trainer, activeTab, fetchRelatedEntities]);

  return {
    // Core data
    id,
    trainer,
    monsters,
    galleryImages,
    loading,
    error,
    isOwner,
    filteredMonsters,

    // Tab state
    activeTab,
    setActiveTab,

    // PC box
    currentPCBox,
    setCurrentPCBox,
    searchTerm,
    setSearchTerm,
    viewMode,
    setViewMode,
    getMaxBoxNumber,
    getBoxMonsters,
    getBoxMonstersForDisplay,
    getFilteredBoxMonsters,

    // Edit boxes
    boxMonsters,
    featuredMonsters,
    setFeaturedMonsters,
    featuredMonstersCollapsed,
    setFeaturedMonstersCollapsed,
    isSaving,
    statusMessage,
    statusType,
    setStatusMessage,
    setStatusType,
    handleDragStart,
    handleDragOver,
    handleDragEnter,
    handleDragLeave,
    handleDrop,
    handleDragEnd,
    handleFeaturedDrop,
    handleFeaturedDragOver,
    handleFeaturedDragLeave,
    handleSaveBoxes,
    handleSaveFeaturedMonsters,
    handleAddBox,
    boxSettings,
    isBoxLocked,
    isDefaultBox,
    toggleBoxLock,
    setDefaultBox: setDefaultBox,
    lockedBoxNumbers,
    showAutoSortModal,
    setShowAutoSortModal,
    handleApplySort,
    handleSwapBoxes,
    handleInsertBoxBefore,

    // Inventory
    inventoryData,
    inventoryItemDetails,
    isItemDetailModalOpen,
    setIsItemDetailModalOpen,
    selectedItemForDetail,
    handleItemDetailClick,
    getItemImageUrl,

    // Achievements
    achievements,
    achievementStats,
    achievementsLoading,
    achievementFilter,
    setAchievementFilter,
    handleClaimAchievement,
    handleClaimAllAchievements,
    isClaimingAll,

    // Reward popup
    showRewardPopup,
    setShowRewardPopup,
    rewardPopupData,

    // Mass edit
    showMassEditModal,
    setShowMassEditModal,
    trainerInventory,
    handleOpenMassEdit,
    handleMassEditComplete,
    hasPendingMassEditSession,

    // Relations
    relatedTrainers,
    relatedMonsters,

    // Image modal
    showImageModal,
    modalImageSrc,
    modalImageAlt,
    handleImageClick,
    closeImageModal,

    // Refresh
    fetchTrainerData,
  };
}
